/**
 * Admin GPU resources page (shadcn/Tailwind, R22.7).
 *
 * Lists every row in omega_engine.registered_hosts. Surfaces all bridge
 * columns (auth_scheme, auth_secret_ref, r2_output_prefix) so the admin
 * UI is the single operational interface for the GPU control plane.
 *
 * Composition:
 *   - shadcn Card → filter bar (status / datacenter / auth_scheme selects)
 *   - DataTable (TanStack)  → 10-column table, sortable, paginated
 *   - shadcn DropdownMenu  → per-row actions (Edit, Delete)
 *   - shadcn AlertDialog   → destructive-action confirmation
 *   - shadcn Badge / Tooltip → status, auth scheme, health
 *   - sonner toasts        → mutation feedback
 */
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, RefreshCw, MoreHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteGpuHost,
  listGpuHosts,
  type AuthScheme,
  type GpuHost,
  type HostStatus,
  type ListGpuHostsQuery,
} from '@/services/api/gpu-hosts.js';
import { DataTable } from '@/components/data-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { GpuHostFormModal } from './GpuHostFormModal.js';

const STATUS_OPTIONS: { value: HostStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'drain', label: 'Drain' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'maintenance', label: 'Maintenance' },
];

const AUTH_SCHEME_OPTIONS: { value: AuthScheme; label: string }[] = [
  { value: 'service_token', label: 'service_token' },
  { value: 'cf_access', label: 'cf_access' },
  { value: 'mtls', label: 'mtls' },
];

function statusBadgeClass(s: HostStatus): string {
  switch (s) {
    case 'active':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/20';
    case 'drain':
      return 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20';
    case 'disabled':
      return 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/20';
    case 'maintenance':
      return 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
  }
}

function authSchemeBadgeClass(s: AuthScheme): string {
  switch (s) {
    case 'cf_access':
      return 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/20';
    case 'mtls':
      return 'bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/20';
    case 'service_token':
    default:
      return 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-400 border-zinc-500/20';
  }
}

function capabilityBadgeClass(c: string): string {
  if (c === 'comfyui') return 'bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/20';
  if (c === 'vllm') return 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-400 border-cyan-500/20';
  return 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-400 border-zinc-500/20';
}

function HealthBadge({ host }: { host: GpuHost }) {
  if (host.last_health_at === null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const at = new Date(host.last_health_at);
  const ago = Math.floor((Date.now() - at.getTime()) / 1000);
  const agoLabel = ago < 60 ? `${ago}s` : ago < 3600 ? `${Math.floor(ago / 60)}m` : `${Math.floor(ago / 3600)}h`;
  const okClass = host.last_health_ok
    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/20'
    : 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/20';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="outline" className={okClass}>
          {host.last_health_ok ? 'OK' : 'FAIL'} · {agoLabel}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p className="font-mono text-xs">{at.toISOString()}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export default function GpuHostsPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState<ListGpuHostsQuery>({});
  const [editingHost, setEditingHost] = useState<GpuHost | null>(null);
  const [creating, setCreating] = useState(false);
  const [hostToDelete, setHostToDelete] = useState<GpuHost | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['gpu-hosts', filters],
    queryFn: () => listGpuHosts(filters),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteGpuHost(id),
    onSuccess: () => {
      toast.success('Host deleted');
      void qc.invalidateQueries({ queryKey: ['gpu-hosts'] });
    },
    onError: (e: unknown) => {
      toast.error(`Delete failed: ${(e as Error).message}`);
    },
  });

  const datacenters = useMemo(() => {
    const set = new Set<string>();
    for (const h of data?.items ?? []) set.add(h.datacenter);
    return Array.from(set).sort();
  }, [data?.items]);

  const columns = useMemo<ColumnDef<GpuHost, unknown>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 220,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>
        ),
      },
      {
        accessorKey: 'datacenter',
        header: 'DC',
        size: 90,
        cell: ({ row }) => <Badge variant="secondary">{row.original.datacenter}</Badge>,
      },
      {
        accessorKey: 'host_url',
        header: 'Host URL',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.host_url}</span>
        ),
      },
      {
        accessorKey: 'capability_set',
        header: 'Capabilities',
        size: 160,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.capability_set.map((c) => (
              <Badge key={c} variant="outline" className={capabilityBadgeClass(c)}>
                {c}
              </Badge>
            ))}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 110,
        cell: ({ row }) => (
          <Badge variant="outline" className={statusBadgeClass(row.original.status)}>
            {row.original.status}
          </Badge>
        ),
      },
      {
        accessorKey: 'auth_scheme',
        header: 'Auth',
        size: 130,
        cell: ({ row }) => (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className={authSchemeBadgeClass(row.original.auth_scheme)}>
                {row.original.auth_scheme}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="font-mono text-xs">
                secret_ref: {row.original.auth_secret_ref ?? '—'}
              </p>
            </TooltipContent>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'r2_output_prefix',
        header: 'R2 prefix',
        size: 200,
        cell: ({ row }) =>
          row.original.r2_output_prefix ? (
            <span className="font-mono text-xs">{row.original.r2_output_prefix}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'weight',
        header: 'Weight',
        size: 80,
      },
      {
        id: 'health',
        header: 'Health',
        size: 140,
        enableSorting: false,
        cell: ({ row }) => <HealthBadge host={row.original} />,
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setEditingHost(row.original)}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                onClick={() => setHostToDelete(row.original)}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [],
  );

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">GPU resources</h1>
            <p className="text-sm text-muted-foreground">
              Registered hosts in <code className="text-xs">omega_engine.registered_hosts</code>.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add host
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="flex flex-wrap gap-x-6 gap-y-3 p-4">
            <FilterSelect
              label="Status"
              value={filters.status}
              onChange={(v) =>
                setFilters((f) => ({ ...f, status: v as HostStatus | undefined }))
              }
              options={STATUS_OPTIONS}
              placeholder="any"
            />
            <FilterSelect
              label="Datacenter"
              value={filters.datacenter}
              onChange={(v) => setFilters((f) => ({ ...f, datacenter: v }))}
              options={datacenters.map((d) => ({ value: d, label: d }))}
              placeholder="any"
            />
            <FilterSelect
              label="Auth scheme"
              value={filters.auth_scheme}
              onChange={(v) =>
                setFilters((f) => ({ ...f, auth_scheme: v as AuthScheme | undefined }))
              }
              options={AUTH_SCHEME_OPTIONS}
              placeholder="any"
            />
          </CardContent>
        </Card>

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>Failed to load GPU hosts</AlertTitle>
            <AlertDescription>{(error as Error).message}</AlertDescription>
          </Alert>
        ) : null}

        <DataTable<GpuHost>
          columns={columns}
          data={data?.items ?? []}
          isLoading={isLoading}
          emptyMessage="No registered hosts."
        />

        {creating ? (
          <GpuHostFormModal
            mode="create"
            onClose={() => setCreating(false)}
            onSuccess={() => {
              setCreating(false);
              void qc.invalidateQueries({ queryKey: ['gpu-hosts'] });
            }}
          />
        ) : null}

        {editingHost ? (
          <GpuHostFormModal
            mode="edit"
            host={editingHost}
            onClose={() => setEditingHost(null)}
            onSuccess={() => {
              setEditingHost(null);
              void qc.invalidateQueries({ queryKey: ['gpu-hosts'] });
            }}
          />
        ) : null}

        <AlertDialog
          open={!!hostToDelete}
          onOpenChange={(open) => {
            if (!open) setHostToDelete(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this host?</AlertDialogTitle>
              <AlertDialogDescription>
                Removes <span className="font-mono text-xs">{hostToDelete?.id}</span> from the
                gateway pool immediately. Pending jobs already routed to it may fail; new jobs
                will not be dispatched here. This can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (hostToDelete) deleteMut.mutate(hostToDelete.id);
                  setHostToDelete(null);
                }}
              >
                Delete host
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}

interface FilterSelectProps {
  label: string;
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}

function FilterSelect({ label, value, onChange, options, placeholder }: FilterSelectProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <Select
        value={value ?? '__any__'}
        onValueChange={(v) => onChange(v === '__any__' ? undefined : v)}
      >
        <SelectTrigger className="h-8 w-[160px]">
          <SelectValue placeholder={placeholder ?? 'any'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__any__">any</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
